<template>
    <modal
        v-if="show"
        :form="form"
        class="toolFreeComplianceAI"
        :cardClass="'toolFreeAI'"
        :type="'is-primary'"
        :size="'is-small'"
        :title="titleModal"
        :cancelText="'Cancel'"
        :backText="'Back'"
        :submitClasses="{
            'is-primary': true,
            'is-busy': form.busy || isLoading || profileBusinessLoading,
        }"
        :submitText="submitButtonText"
        :backDisabled="form.busy"
        :isCloseDisabled="form.busy"
        :submitDisabled="isDisabledSubmitButton"
        :hideBack="!isShowBack"
        :hideCancel="!isShowCancel"
        :hideFoot="isGenerating"
        @back="handleBack"
        @closed="handleClosed"
        @submit="nextStep"
    >
        <configuration-stepper
            v-if="!isLoading && !item"
            :type="'simple'"
            :currentStep="step"
            :steps="steps"
        />
        <div v-if="isLoading" class="complianceModal__content">
            <basic-loader :center="true" aria-label="TollFreeVerificationAI" />
        </div>
        <div v-else class="content">
            <template v-if="currentStep === MODAL_TYPES.ADD_NUMBERS">
                <add-numbers
                    :item="item"
                    :form="form"
                    :number="numberProps"
                    :isNumberView="isNumberView"
                    :phoneUpdateData="phoneUpdateData"
                    :getAvailableNumbers="getAvailableNumbers"
                    :isShowExplanation="isShowExplanation"
                    :disabledNumbersStep="disabledNumbersStep"
                    :isAllItemStepsCompleted="isAllItemStepsCompleted"
                    :isAllTollFreeNumbersVerified="isAllTollFreeNumbersVerified"
                />
            </template>
            <template v-if="currentStep === MODAL_TYPES.BUSINESS_INFO">
                <div class="content__ein">
                    <search-verify-ein @select="insertCompanyInfo" />
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <label class="label">Country</label>
                        <countries-dropdown
                            :countries="countries"
                            :selectedOption="form.business_information.country"
                            @select-option="selectCountry"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.business_information.business_name"
                            label="Business name"
                            hideLabelColon
                            :placeholder="'e.g. Example Inc.'"
                            :error="
                                form.errors.has('business_information.business_name')
                                    ? form.errors.get('business_information.business_name')
                                    : form.errors.get('business_name')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.business_information.website_url"
                            label="Business Website"
                            hideLabelColon
                            :placeholder="'Enter website'"
                            :error="
                                form.errors.has('business_information.website_url')
                                    ? form.errors.get('business_information.website_url')
                                    : form.errors.get('website_url')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.business_information.street_address_1"
                            label="Address line 1"
                            hideLabelColon
                            :placeholder="'e.g. Acme Street 1'"
                            :error="
                                form.errors.has('business_information.street_address_1')
                                    ? form.errors.get('business_information.street_address_1')
                                    : form.errors.get('street_address_1')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.business_information.street_address_2"
                            label="Address line 2 (optional)"
                            hideLabelColon
                            :placeholder="'e.g. Acme Street 2'"
                            :error="
                                form.errors.has('business_information.street_address_2')
                                    ? form.errors.get('business_information.street_address_2')
                                    : form.errors.get('street_address_2')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.business_information.city"
                            label="City"
                            hideLabelColon
                            :placeholder="'e.g. Los Angeles'"
                            :error="
                                form.errors.has('business_information.city')
                                    ? form.errors.get('business_information.city')
                                    : form.errors.get('city')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-6">
                        <form-dropdown
                            id="dropdown-state_id"
                            ref="stateSelect"
                            :key="form.business_information.country"
                            v-model="form.business_information.state_id"
                            label="State / Province"
                            hideLabelColon
                            :placeholder="'e.g. California'"
                            :error="
                                form.errors.has('business_information.state_id')
                                    ? form.errors.get('business_information.state_id')
                                    : form.errors.get('state_id')
                            "
                            :settings="{
                                dropdownParent: 'body',
                            }"
                            :disabled="form.busy || isDisabledState"
                        >
                            <option
                                v-for="state in arrStatesOfCountry"
                                :key="state.id"
                                :value="state.id"
                            >
                                {{ state.name }}
                            </option>
                        </form-dropdown>
                    </div>
                    <div class="column is-6">
                        <form-input
                            v-model="form.business_information.postal_code"
                            label="ZIP / Postal code"
                            hideLabelColon
                            :placeholder="'e.g. 90011'"
                            :maxlength="
                                form.business_information.country === 'US'
                                    ? 5
                                    : form.business_information.country === 'CA'
                                        ? 6
                                        : 7
                            "
                            :error="
                                form.errors.has('business_information.postal_code')
                                    ? form.errors.get('business_information.postal_code')
                                    : form.errors.get('postal_code')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
            </template>
            <template v-if="currentStep === MODAL_TYPES.BUSINESS_POINTS_OF_CONTACT">
                <form-input v-model="form.points_of_contact.init" type="hidden" />
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.points_of_contact.first_name"
                            label="Business contact first name"
                            hideLabelColon
                            :placeholder="'e.g. Marty'"
                            :value="form.points_of_contact.first_name"
                            :error="
                                form.errors.has('points_of_contact.first_name')
                                    ? form.errors.get('points_of_contact.first_name')
                                    : form.errors.get('contact_person.first_name')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.points_of_contact.last_name"
                            label="Business contact last name"
                            hideLabelColon
                            :placeholder="'e.g. Byrde'"
                            :value="form.points_of_contact.last_name"
                            :error="
                                form.errors.has('points_of_contact.last_name')
                                    ? form.errors.get('points_of_contact.last_name')
                                    : form.errors.get('contact_person.last_name')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-6">
                        <form-input
                            v-model="form.points_of_contact.business_title"
                            label="Business Title"
                            :hideLabelColon="true"
                            :placeholder="'e.g. Founder'"
                            :error="
                                form.errors.has('points_of_contact.business_title')
                                    ? form.errors.get('points_of_contact.business_title')
                                    : form.errors.get('contact_person.business_title')
                            "
                            :disabled="form.busy"
                            tooltip="We’re just making sure you’re in a position to make important decisions. So, president, founder, executive, manager, or even boss are legit options"
                        />
                    </div>

                    <div class="column is-6">
                        <form-dropdown
                            v-model="form.points_of_contact.job_position_id"
                            label="Job Position"
                            dropdown-class="selectize-contacts"
                            :hideLabelColon="true"
                            :placeholder="'e.g. CEO'"
                            :error="
                                form.errors.has('points_of_contact.job_position_id')
                                    ? form.errors.get('points_of_contact.job_position_id')
                                    : form.errors.get('contact_person.job_position_id')
                            "
                            :settings="dropdownSettingsJobPositions"
                            :disabled="form.busy"
                            tooltip="Pick CEO if you’re unsure of your role. It looks good on a resume"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.points_of_contact.email"
                            type="email"
                            label="Business email"
                            hideLabelColon
                            :placeholder="'e.g. info@theozarks.com'"
                            :value="form.points_of_contact.email"
                            :error="
                                form.errors.has('points_of_contact.email')
                                    ? form.errors.get('points_of_contact.email')
                                    : form.errors.get('contact_person.email')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-12">
                        <form-input
                            v-model="form.points_of_contact.phone_number"
                            label="Phone Number"
                            hideLabelColon
                            :placeholder="'e.g. (718) 737-9809'"
                            :value="form.points_of_contact.phone_number"
                            :error="
                                form.errors.has('points_of_contact.phone_number')
                                    ? form.errors.get('points_of_contact.phone_number')
                                    : form.errors.get('contact_person.phone_number')
                            "
                            :disabled="form.busy"
                        />
                    </div>
                </div>
            </template>
            <template v-if="currentStep === MODAL_TYPES.CHOOSE_USE_CASE">
                <div class="content__ein">
                    <div class="content__use-case-label">Please select your texting use case</div>
                    <div class="content__use-case">
                        <form-dropdown
                            id="dropdown-use_case_id"
                            v-model="form.use_case_id"
                            dropdownClass="dropdown-use-case"
                            placeholder="e.g. Marketing"
                            :settings="dropdownSettingsUseCases"
                            :error="form.errors.get('use_case_id')"
                            :hideLabelColon="true"
                            :hideRequired="true"
                            :disabled="form.busy"
                        />
                        <button
                            class="button is-primary"
                            :disabled="!isUseCaseSelected || isGenerating"
                            @click.prevent="runGeneratingUseCase"
                        >
                            Generate
                        </button>
                    </div>
                </div>
                <div v-if="isGenerating" class="generatingLoader">
                    <circle-loader :size="48" aria-label="TollFreeVerificationAI" />
                    <span class="generatingLoader__brand">
                        <img
                            :src="`${$window.Config.cdn_url}/images/logo-icon-blue.png`"
                            alt="Salesmsg Logo"
                        />
                        <span>Salesmsg AI</span>
                    </span>
                    <span class="generatingLoader__description">Generating your use case...</span>
                </div>
                <template v-else>
                    <div class="columns">
                        <div class="column is-12">
                            <form-textarea
                                v-model="form.generated_summary"
                                label="Use case description"
                                placeholder="Enter use case description"
                                :rows="3"
                                :error="generatedSummaryError"
                                :tooltip="'Review and edit the Use Case Summary as needed (Max 500 characters)'"
                                :hideLabelColon="true"
                                :disabled="form.busy"
                            />
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column is-12">
                            <form-textarea
                                v-model="form.generated_message"
                                label="Sample message"
                                placeholder="Enter sample message"
                                :rows="3"
                                :error="generatedMessageError"
                                :tooltip="'Review and edit the Sample Message as needed (Max 500 characters)'"
                                :hideLabelColon="true"
                                :disabled="form.busy"
                            />
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column is-12">
                            <form-dropdown
                                id="dropdown-monthly_volume"
                                v-model="form.monthly_volume"
                                :settings="dropdownSettingsMonthly"
                                label="Expected monthly messages"
                                :placeholder="placeholderMonthly"
                                :error="form.errors.get('monthly')"
                                :tooltip="'Estimate your monthly message volume across all the numbers you intend to include in this use case'"
                                :hideLabelColon="true"
                                :hideRequired="true"
                                :disabled="form.busy"
                                @focus="handleMonthlyDropdownStatus"
                            />
                        </div>
                    </div>
                </template>
            </template>
            <template v-if="currentStep === MODAL_TYPES.OPT_IN">
                <opt-in
                    isAI
                    :opt_in_data_server="opt_in_data_server"
                    :opt_in_data_error="opt_in_data_error"
                    :show-policy-options-error="showOptInPolicyOptionsError"
                    :show-files-error="showOptInFilesError"
                    @on-change="handleOptIn"
                    @on-delete-file="handleDeleteOptInFile"
                    @handle-update-state="handleUpdateState"
                />
            </template>
        </div>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import SearchVerifyEin from './SearchVerifyEin.vue';
import OptIn from 'views/Settings/Organization/Settings/Compliance/TollFreeVerificationModal/Steps/OptIn/OptIn.vue';
import AddNumbers from 'views/Settings/Organization/Settings/Compliance/TollFreeVerificationModal/Steps/AddNumbers/AddNumbers.vue';
import { cloneDeep } from 'lodash';
import CountriesDropdown from 'views/Settings/Organization/Settings/Compliance/BusinessProfileModal/CountriesDropdown.vue';

const MODAL_TYPES = {
    ADD_NUMBERS: 1,
    BUSINESS_INFO: 2,
    BUSINESS_POINTS_OF_CONTACT: 3,
    CHOOSE_USE_CASE: 4,
    OPT_IN: 5,
};

export default {
    components: {
        CountriesDropdown,
        AddNumbers,
        OptIn,
        SearchVerifyEin,
    },

    props: {
        numberProps: { type: Object, default: null },
    },

    data() {
        return {
            form: new Form({
                business_information: {
                    website_url: '',
                    business_name: '',
                    street_address_1: '',
                    street_address_2: '',
                    city: '',
                    state_id: '',
                    country: '',
                    postal_code: '',
                    ein: '',
                },
                points_of_contact: {
                    init: false,
                    first_name: '',
                    last_name: '',
                    email: '',
                    business_title: '',
                    job_position_id: '',
                    phone_number: '',
                },
                generated_summary: '',
                generated_message: '',
                numbers: [],
                explanation_description: '',
                title: '',
                use_case_id: '',
                monthly_volume: '',
                opt_in_methods: null,
            }),
            opt_in_data_error: null,
            opt_in_data_server: [],
            opt_in_data: {},
            opt_in_options_state: false,
            opt_in_files: [],
            isLoading: false,
            isGenerating: false,
            currentStepIndex: 0,
            MODAL_TYPES: MODAL_TYPES,
            countries: [],
            arrStates: [],
            arrStatesOfCountry: [],
            arrJobPositions: [],
            isActiveMonthlyDropdown: false,
            validateLoadingFileOptIn: false,
            isOptInCompleted: false,
            showOptInPolicyOptionsError: false,
            showOptInFilesError: false,
            isAllTollFreeNumbersVerified: false,
            isCreateTollFreeProfileClicked: false,
            isOnTrialLocal: false,
            currentTollFreeProfileId: null,
            generatedSummaryError: '',
            generatedMessageError: '',
        };
    },

    computed: {
        titleModal() {
            if (this.dataForRecovered) {
                if (this.isNumberView) return 'Update Toll-Free Verification';

                return 'Edit Toll-Free Verification';
            }

            return 'Toll-Free Verification';
        },

        stepsAccess() {
            return [
                MODAL_TYPES.ADD_NUMBERS,
                MODAL_TYPES.BUSINESS_INFO,
                MODAL_TYPES.BUSINESS_POINTS_OF_CONTACT,
                MODAL_TYPES.CHOOSE_USE_CASE,
                MODAL_TYPES.OPT_IN,
            ];
        },

        currentStep() {
            return this.stepsAccess[this.currentStepIndex];
        },

        step() {
            return this.currentStep;
        },

        steps() {
            return this.stepsAccess?.length || 5;
        },

        phoneUpdateData() {
            return this.showTollFreeVerificationModalPhoneUpdateData;
        },

        disabledNumbersStep() {
            return this.showTollFreeVerificationModalDisabledNumbersStep;
        },

        modalType() {
            return this.showTollFreeVerificationModalType;
        },

        isNumberView() {
            return this.modalType === 'numberView';
        },

        isNumberAdd() {
            return this.modalType === 'numberAdd';
        },

        isValidAddNumbers() {
            return !(
                !this.form.numbers?.length ||
                (this.form.numbers?.length > 1 && !this.form.explanation_description?.length)
            );
        },

        isShowExplanation() {
            return Boolean(
                this.form.numbers?.length > 1 || (this.item && this.item.numbers.length)
            );
        },

        isDisabledSubmitButton() {
            const status = Boolean(
                !this.isNumberView &&
                    this.isShowExplanation &&
                    this.item &&
                    this.item.numbers.length &&
                    !this.form.explanation_description.length
            );

            return Boolean(
                this.currentStep === MODAL_TYPES.ADD_NUMBERS && (!this.isValidAddNumbers || status)
            );
        },

        isDisabledState() {
            return Boolean(
                this.form.business_information.country &&
                    !(
                        this.form.business_information.country === 'US' ||
                        this.form.business_information.country === 'CA'
                    )
            );
        },

        isShowBack() {
            return !this.isShowCancel;
        },

        isShowCancel() {
            return (
                (this.currentStep === MODAL_TYPES.BUSINESS_INFO &&
                    this.dataForRecovered &&
                    this.isNumberView) ||
                this.currentStep === MODAL_TYPES.ADD_NUMBERS
            );
        },

        isOnTrial() {
            if (!this.subscription) return false;

            return Boolean(
                this.organization?.trial_ends_at !== null && this.subscription.status === 'trialing'
            );
        },

        isPftTrialFlow() {
            return this.user.isPftTrialFlow && this.isOnTrialLocal;
        },

        hasBusinessInformationFields() {
            return (
                this.form.business_information.business_name &&
                this.form.business_information.website_url &&
                (this.form.business_information.street_address_1 ||
                    this.form.business_information.street_address_2) &&
                this.form.business_information.city &&
                this.form.business_information.postal_code &&
                this.form.business_information.state_id
            );
        },

        hasUserFields() {
            return (
                this.form.points_of_contact.first_name &&
                this.form.points_of_contact.last_name &&
                this.form.points_of_contact.business_title &&
                this.form.points_of_contact.job_position_id &&
                this.form.points_of_contact.email &&
                this.form.points_of_contact.phone_number
            );
        },

        hasGeneratedFields() {
            return this.form.generated_summary && this.form.generated_message;
        },

        hasGeneratedFieldsFilled() {
            return this.hasGeneratedFields && this.form.monthly_volume;
        },

        isUseCaseSelected() {
            return this.form.use_case_id;
        },

        submitButtonText() {
            if (this.currentStep === MODAL_TYPES.OPT_IN) {
                return 'Verifiy now';
            }

            if (this.currentStep === MODAL_TYPES.ADD_NUMBERS) {
                return this.isAllItemStepsCompleted ? 'Submit for review' : 'Continue';
            }

            return 'Continue';
        },

        getAvailableNumbers() {
            const currentNumbersList = [
                ...this.numbers.filter(
                    (item) =>
                        item.isTollFree &&
                        !item.isSendToTollFreeVerification &&
                        !item.statusInternalReview &&
                        !item.isAircall
                ),
                ...this.unassignedNumbers.filter(
                    (item) =>
                        item.isTollFree &&
                        !item.isSendToTollFreeVerification &&
                        !item.statusInternalReview &&
                        !item.isAircall
                ),
            ];

            if (this.dataForRecovered) {
                const ids = this.dataForRecovered.numbers.map((item) => item.id);
                const numbers = [];

                const allUnverifiedTollFreeNumbers = [
                    ...this.numbers.filter((item) => item.isTollFree),
                    ...this.unassignedNumbers.filter((item) => item.isTollFree),
                ];

                const recoveredList = [];

                ids.forEach((id) => {
                    allUnverifiedTollFreeNumbers.forEach((phone) => {
                        if (phone?.id === id) {
                            recoveredList.push(phone);
                        }
                    });
                });

                [...currentNumbersList, ...recoveredList].forEach((item) => {
                    const exist = Boolean(numbers.find((number) => number.id === item.id));

                    if (!exist) {
                        numbers.push(item);
                    }
                });

                return numbers;
            }

            return currentNumbersList;
        },

        isAllItemStepsCompleted() {
            if (this.isNumberAdd) return true;
            if (this.isNumberView) return false;
            if (this.item) {
                return (
                    this.getTollFreeCaseStep(this.item) === MODAL_TYPES.OPT_IN &&
                    this.item.completed
                );
            }

            return false;
        },

        dropdownSettingsUseCases() {
            const options = this.tollFreeFormFieldsUseCases.map((item) => ({
                id: item.id,
                label: item.title,
            }));

            return {
                maxItems: 1,
                options: options,
                valueField: 'id',
                highlight: false,
                searchField: ['label'],
                render: {
                    item: this.dropdownItem,
                    option: this.dropdownOption,
                },
                dropdownParent: 'body',
            };
        },

        dropdownSettingsMonthly() {
            const options = this.tollFreeFormFieldsMonthlyValues.map((item, index, arr) => {
                if (index === 0) {
                    return {
                        id: item,
                        label: `1-${item}`,
                    };
                }

                return {
                    id: item,
                    label: `${arr[index - 1] + 1}-${item}`,
                };
            });

            return {
                maxItems: 1,
                options: options,
                valueField: 'id',
                highlight: false,
                searchField: ['label'],
                render: {
                    item: this.dropdownItem,
                    option: this.dropdownOption,
                },
                dropdownParent: 'body',
            };
        },

        dropdownSettingsJobPositions() {
            return {
                valueField: 'id',
                searchField: ['name'],
                options: this.arrJobPositions,
                highlight: true,
                render: {
                    item: this.dropdownJobPositionItem,
                    option: this.dropdownJobPositionOption,
                },
                maxItems: 1,
                dropdownParent: 'body',
            };
        },

        placeholderMonthly() {
            if (this.form.use_case_id && this.isActiveMonthlyDropdown) {
                const useCase = this.tollFreeFormFieldsUseCases.find(
                    (item) => item.id === Number(this.form.use_case_id)
                );

                return `e.g. ${useCase.title}`;
            }

            return 'e.g. 1,001 - 10,000';
        },

        currentItem() {
            return this.tollFreeProfileItems.find((item) => item.id === this.dataForRecovered?.id);
        },

        originTollFree() {
            return this.tollFreeProfileItems.find(
                (item) => item.id === this.currentTollFreeProfileId
            );
        },

        item() {
            return this.showTollFreeVerificationItem;
        },

        show() {
            return this.showTollFreeVerificationModal;
        },

        dataForRecovered() {
            return this.showTollFreeVerificationDataForRecoveredItem;
        },

        ...mapState(stores.User, ['user']),
        ...mapState(stores.Inboxes, ['inbox', 'currentInboxTypeId', 'currentInboxType']),
        ...mapState(stores.Number, ['number', 'numbers', 'unassignedNumbers']),
        ...mapState(stores.ComplianceProfile, [
            'tollFreeFormFieldsUseCases',
            'tollFreeFormFieldsMonthlyValues',
            'profileBusinessLoading',
            'profileBusiness',
            'profileBusinessStates',
            'profileBusinessJobPositions',
            'tollFreeProfileItems',
            'tollFreeProfileCreateError',
            'complianceModalsOrder',
        ]),
        ...mapState(stores.TollFreeVerificationAi, [
            'tollFreeVerificationSummaryMessage',
            'tollFreeVerificationSampleMessage',
        ]),
        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.Organization, ['organization']),
        ...mapState(stores.TollFreeStore, [
            'showTollFreeVerificationItem',
            'showTollFreeVerificationModal',
            'showTollFreeVerificationDataForRecoveredItem',
            'showTollFreeVerificationModalType',
            'showTollFreeVerificationModalDisabledNumbersStep',
            'showTollFreeVerificationModalPhoneUpdateData',
        ]),
    },

    watch: {
        show(value) {
            if (value) {
                this.init();
            }
        },

        tollFreeVerificationSummaryMessage(message) {
            this.form.generated_summary = message;
        },

        'form.generated_summary'() {
            this.generatedSummaryError = '';
        },

        tollFreeVerificationSampleMessage(message) {
            this.form.generated_message = message;
        },

        'form.generated_message'() {
            this.generatedMessageError = '';
        },

        'form.monthly_volume'() {
            this.form.errors.clear('monthly');
        },

        tollFreeProfileCreateError() {
            this.isCreateTollFreeProfileClicked = false;
        },

        item(value) {
            if (this.isNumberView) return;
            if (this.isShowExplanation) return;

            if (value && this.isAllItemStepsCompleted && !this.numberProps) {
                this.form.explanation_description = value.explanation_description || '';
            } else if (value && this.isAllItemStepsCompleted && this.numberProps) {
                this.initTollFreeForm(value);
                this.initStep(value);
            } else {
                this.initTollFreeForm(this.item);
                this.initStep(this.item);
            }
        },

        'form.use_case_id'(value) {
            const useCase = this.tollFreeFormFieldsUseCases.find(
                (item) => item.id === Number(value)
            );

            if (useCase) {
                this.form.title = useCase.title ?? '';
            }
        },
    },

    methods: {
        async init() {
            this.reset();
            this.isLoading = true;
            this.isOnTrialLocal = this.isOnTrial;

            await Promise.all([
                this.initCountries(),
                this.initNumbers(),
                this.initBusinessProfileFields(),
                this.initTollFreeFormFields(),
            ]);

            this.arrStates = this.profileBusinessStates;
            this.arrJobPositions = this.profileBusinessJobPositions;

            if (
                Array.isArray(this.profileBusiness?.pointsOfContact) &&
                Array.isArray(this.profileBusinessJobPositions)
            ) {
                this.profileBusiness.pointsOfContact.forEach((contact) => {
                    const existJobPosition = this.profileBusinessJobPositions.find(
                        (jopPosition) => contact?.job_position_id === jopPosition.id
                    );
                    if (!existJobPosition && contact.job_position !== null) {
                        this.arrJobPositions.push(contact.job_position);
                    }
                });
            }

            this.insertCompanyInfo(this.profileBusiness?.businessInformation);
            this.initUserFields(this.profileBusiness?.pointsOfContacts[0]);
            this.setCompanyStatesList();

            if (this.numberProps && !this.item && !this.dataForRecovered) {
                this.initStep();
            }

            if (this.dataForRecovered) {
                this.initTollFreeForm(this.dataForRecovered);
                this.initStep(this.dataForRecovered);
            }

            this.isLoading = false;
        },

        async initBusinessProfileFields() {
            await Promise.all([
                this.$store.dispatch('fetchBusinessProfileStates'),
                this.$store.dispatch('fetchBusinessProfileJobPositions'),
            ]);

            await this.$store.dispatch('fetchBusinessProfile');
        },

        async initTollFreeFormFields() {
            try {
                await this.$store.dispatch('fetchTollFreeFormFields');
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'initTollFreeFormFields');
            }
        },

        async initTollFreeForm(data) {
            if (data) {
                if (this.isNumberView && this.phoneUpdateData?.individual_toll_free_profile?.id) {
                    const individualTollFreeProfile =
                        this.phoneUpdateData.individual_toll_free_profile;

                    this.currentTollFreeProfileId = individualTollFreeProfile.id;
                    this.form.title = individualTollFreeProfile.title;
                    this.form.monthly_volume = individualTollFreeProfile.monthly_volume;
                    this.form.use_case_id = individualTollFreeProfile.use_case_id;
                    this.form.generated_summary = individualTollFreeProfile.use_case_description;
                    this.form.generated_message = individualTollFreeProfile.sample_message_1;
                    this.form.explanation_description = individualTollFreeProfile.explanation || '';

                    this.initOptInData(individualTollFreeProfile.opt_in_data);
                } else {
                    this.currentTollFreeProfileId = data.id;
                    this.form.title = data.title;
                    this.form.monthly_volume = data.monthly_volume;
                    this.form.use_case_id = data.use_case_id;
                    this.form.generated_summary = data.use_case_description;
                    this.form.generated_message = data.sample_message_1;
                    this.form.explanation_description = data.explanation_description || '';
                    this.form.numbers = data.numbers.map((item) => item.id);

                    this.initOptInData(data.opt_in_data);
                }

                this.form.opt_in_methods = data.opt_in_methods.map((item) => item.id);
            }
        },

        async initStep(data = null) {
            this.form.busy = true;
            let modalType;
            if (
                !this.hasBusinessInformationFields ||
                (this.dataForRecovered && this.isNumberView)
            ) {
                modalType = MODAL_TYPES.BUSINESS_INFO;
            } else if (!this.hasUserFields) {
                modalType = MODAL_TYPES.BUSINESS_POINTS_OF_CONTACT;
            } else {
                modalType = MODAL_TYPES.CHOOSE_USE_CASE;
            }

            const currentStep = this.isNumberView
                ? modalType
                : this.numberProps
                    ? MODAL_TYPES.ADD_NUMBERS
                    : this.getTollFreeCaseStep(data);

            if (currentStep === MODAL_TYPES.ADD_NUMBERS) {
                await this.initNumbers();
                this.checkAvailableNumbersList();
            }

            this.setStep(currentStep);
            this.form.busy = false;
        },

        initOptInData(opt_in_data) {
            if (opt_in_data?.length > 0) {
                this.opt_in_data_server = cloneDeep(opt_in_data);
            } else {
                if (this.originTollFree) {
                    this.opt_in_data_server = cloneDeep(this.originTollFree.opt_in_data);
                }
            }
        },

        initUserFields(data) {
            this.form.points_of_contact.init = true;
            if (data) {
                this.form.points_of_contact.first_name = data?.first_name;
                this.form.points_of_contact.last_name = data?.last_name;
                this.form.points_of_contact.email = data?.email;
                this.form.points_of_contact.business_title = data?.business_title;
                this.form.points_of_contact.job_position_id = data?.job_position?.id;
                this.form.points_of_contact.phone_number = data?.phone_number;
            } else {
                this.form.points_of_contact.first_name =
                    this.user.first_name || this.user.firstName;
                this.form.points_of_contact.last_name = this.user.last_name || this.user.lastName;
                this.form.points_of_contact.email = this.user.email;
                this.form.points_of_contact.phone_number = this.user.number;
            }
        },

        async initCountries() {
            try {
                const res = await this.$store.dispatch('loadCountries');
                this.countries = res?.data || [];
            } catch (e) {
                this.countries = [];
            }
        },

        async initNumbers() {
            try {
                await this.$store.dispatch('getNumbers');
                await this.$store.dispatch('getNumbersUnassigned');
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'initNumbers');
            }
        },

        initValidateMessageLength(key) {
            if (this.form[key] && this.form[key].length > 500) {
                if (key === 'generated_summary') {
                    this.generatedSummaryError =
                        'The Toll-Free Use Case Description may not be greater than 500 characters';
                } else {
                    this.generatedMessageError =
                        'The Toll-Free Sample Message may not be greater than 500 characters';
                }

                return false;
            }

            if (!this.form[key] || (this.form[key] && this.form[key].trim() === '')) {
                if (key === 'generated_summary') {
                    this.generatedSummaryError =
                        'The Toll-Free Use Case Description field is required';
                } else {
                    this.generatedMessageError = 'The Toll-Free Sample Message field is required';
                }

                return false;
            }

            return true;
        },

        validateMonthlyVolume() {
            if (!this.form.monthly_volume) {
                this.form.errors.record({
                    monthly: ['The Toll-Free Expected Monthly Messages field is required'],
                });

                return false;
            }

            return true;
        },

        async handleAddAdditionalNumbers() {
            try {
                this.form.busy = true;
                const numbersArr = [
                    ...this.item.numbers.map((item) => item.id),
                    ...this.form.numbers,
                ];

                this.form.numbers = [...new Set(numbersArr)];
                const preparedForm = new Form({
                    numbers: this.form.numbers,
                    explanation_description: this.form.explanation_description,
                });

                await this.$store.dispatch('updateTollFreeProfile', {
                    form: preparedForm,
                    id: this.item.id,
                });
                this.form.busy = true;
                await this.handleFetchTollFreeVerify(this.item.id);
                await this.$store.dispatch('fetchTollFreeProfile');
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleAddAdditionalNumbers');
            } finally {
                this.form.busy = false;
                this.handleUpdateNumbersList();
                this.handleClosed();
            }
        },

        validateChooseUseCase() {
            let result = this.initValidateMessageLength('generated_message');
            result &= this.initValidateMessageLength('generated_summary');
            result &= this.validateMonthlyVolume();

            return result;
        },

        async createTollFreeProfile() {
            let currentTollFreeProfile;
            this.form.busy = true;

            const preparedForm = new Form(
                {
                    numbers: this.form.numbers,
                    explanation_description: this.form.explanation_description,
                    title: this.form.title,
                    use_case_id: this.form.use_case_id,
                    use_case_description: this.form.generated_summary,
                    monthly_volume: this.form.monthly_volume,
                    opt_in_methods: this.form.opt_in_methods,
                    sample_message_1: this.form.generated_message,
                },
                false,
                false
            );

            try {
                currentTollFreeProfile = await this.$store.dispatch(
                    'createTollFreeProfile',
                    preparedForm
                );
            } finally {
                this.currentTollFreeProfileId = currentTollFreeProfile?.id;
                this.form.busy = false;
            }

            if (currentTollFreeProfile) {
                this.setStep(this.stepsAccess[this.currentStepIndex + 1]);
            }
        },

        async nextStep() {
            if (this.isAllItemStepsCompleted) {
                await this.handleAddAdditionalNumbers();

                return;
            }

            if (this.currentStep === MODAL_TYPES.ADD_NUMBERS) {
                this.setStep(this.stepsAccess[this.currentStepIndex + 1]);
                return;
            }
            if (this.currentStep === MODAL_TYPES.BUSINESS_INFO) {
                await this.requestIntoSteps('business_information', this.currentStep);
                return;
            }

            if (this.currentStep === MODAL_TYPES.BUSINESS_POINTS_OF_CONTACT) {
                if (this.form.points_of_contact.phone_number) {
                    let errors = {};
                    const primaryNumberLookup = await this.$store.dispatch(
                        'getNumbersLookup',
                        encodeURIComponent(this.form.points_of_contact.phone_number)
                    );

                    if (
                        primaryNumberLookup.data.error ||
                        (primaryNumberLookup.data.type &&
                            primaryNumberLookup.data.type !== 'mobile' &&
                            primaryNumberLookup.data.type !== 'Not Available')
                    ) {
                        errors = {
                            ...errors,
                            'points_of_contact.phone_number': [
                                'Please enter a valid mobile number',
                            ],
                        };
                    }

                    if (Object.keys(errors).length > 0) {
                        this.form.errors.record(errors);
                        return;
                    }
                }

                await this.requestIntoSteps('points_of_contact', this.currentStep);
                return;
            }

            if (this.currentStep === MODAL_TYPES.CHOOSE_USE_CASE) {
                try {
                    if (!this.validateChooseUseCase()) {
                        return;
                    }

                    this.form.busy = true;

                    if (this.currentTollFreeProfileId) {
                        this.isCreateTollFreeProfileClicked = true;

                        const preparedForm = new Form(
                            {
                                numbers: this.form.numbers,
                                explanation_description: this.form.explanation_description,
                                title: this.form.title,
                                use_case_id: this.form.use_case_id,
                                use_case_description: this.form.generated_summary,
                                monthly_volume: this.form.monthly_volume,
                                opt_in_methods: this.form.opt_in_methods,
                                sample_message_1: this.form.generated_message,
                            },
                            false,
                            false
                        );

                        await this.$store.dispatch('updateTollFreeProfile', {
                            form: preparedForm,
                            id: this.currentTollFreeProfileId,
                        });

                        this.setStep(this.stepsAccess[this.currentStepIndex + 1]);
                    } else {
                        this.isCreateTollFreeProfileClicked = true;
                        await this.createTollFreeProfile();
                    }

                    this.isCreateTollFreeProfileClicked = false;
                } catch (e) {
                    this.$log.error(e, this.$options.__file, 'handleNextStep');
                } finally {
                    this.form.busy = false;
                }

                return;
            }

            if (this.currentStep === MODAL_TYPES.OPT_IN) {
                if (!this.isOptInCompleted) {
                    this.showOptInPolicyOptionsError = !this.opt_in_options_state;
                    this.showOptInFilesError = !this.opt_in_files || !this.opt_in_files.length;
                    return;
                }

                try {
                    this.form.busy = true;
                    const preparedForm = new Form(
                        {
                            numbers: this.form.numbers,
                            explanation_description: this.form.explanation_description,
                            title: this.form.title,
                            use_case_id: this.form.use_case_id,
                            use_case_description: this.form.generated_summary,
                            monthly_volume: this.form.monthly_volume,
                            opt_in_methods: this.form.opt_in_methods,
                        },
                        false,
                        false
                    );

                    await this.$store.dispatch('updateTollFreeProfile', {
                        form: preparedForm,
                        id: this.currentTollFreeProfileId,
                    });
                    this.form.busy = true;

                    let filteredOptInData;

                    for (const key in this.opt_in_data) {
                        const item = this.opt_in_data[key];

                        filteredOptInData = {
                            [key]: {
                                opt_in_method_id: item.opt_in_method_id,
                                relatable_service_type: item.relatable_service_type,
                            },
                        };
                    }

                    await this.$store.dispatch('updateOptInSamples', {
                        opt_in_data: filteredOptInData,
                        profile_id: this.currentTollFreeProfileId,
                    });

                    await this.$store.dispatch('fetchTollFreeProfile');
                    await this.updateOptInData('opt_in_method_id');
                    await this.handleUploadFiles();
                    this.opt_in_data_error = null;
                    await this.handleFetchTollFreeVerify(this.currentTollFreeProfileId);
                    await this.$store.dispatch('fetchTollFreeProfile');
                    this.initOptInData();
                    await this.$store.dispatch('getUserMe');
                    this.handleUpdateNumbersList();
                } catch (e) {
                    this.$log.error(e, this.$options.__file, 'verify');

                    if (e?.response?.data) {
                        this.opt_in_data_error = e.response.data;
                    }
                }

                this.form.busy = false;
                this.handleClosed();
            }
        },

        handleBack() {
            if (this.currentStep === MODAL_TYPES.ADD_NUMBERS) {
                this.handleClosed();
                return;
            }

            if (this.currentStep === MODAL_TYPES.BUSINESS_INFO) {
                this.setStep(this.stepsAccess[this.currentStepIndex - 1]);
                return;
            }

            if (this.currentStep === MODAL_TYPES.BUSINESS_POINTS_OF_CONTACT) {
                this.setStep(this.stepsAccess[this.currentStepIndex - 1]);
                return;
            }

            if (this.currentStep === MODAL_TYPES.CHOOSE_USE_CASE) {
                this.setStep(this.stepsAccess[this.currentStepIndex - 1]);
                return;
            }

            if (this.currentStep === MODAL_TYPES.OPT_IN) {
                this.showOptInPolicyOptionsError = false;
                this.showOptInFilesError = false;
                this.setStep(this.stepsAccess[this.currentStepIndex - 1]);
            }
        },

        async handleUpdateNumbersList() {
            await this.$store.dispatch('getNumbers');
            await this.$store.dispatch('getNumbersUnassigned');
            this.$emit('updateNumbersList');
        },

        updateOptInData(key) {
            if (this.originTollFree) {
                const opt_in_data = this.originTollFree.opt_in_data[0];

                if (!opt_in_data) return;

                if (!this.opt_in_data_server[0]) {
                    this.opt_in_data_server = cloneDeep(this.originTollFree.opt_in_data);
                }

                if (key === 'opt_in_method_id') {
                    const is_different = opt_in_data[key] !== this.opt_in_data_server[0][key];

                    this.opt_in_data_server[0][key] = cloneDeep(opt_in_data[key]);

                    if (is_different) {
                        this.opt_in_data_server[0].files = [];
                    }
                }
            }
        },

        setStep(step) {
            this.currentStepIndex = this.getStep(step);
        },

        getStep(step) {
            return this.stepsAccess.findIndex((i) => i === step);
        },

        async handleFetchTollFreeVerify(id) {
            if (this.isNumberView) {
                const number_id = !this.phoneUpdateData?.individual_toll_free_profile
                    ? this.phoneUpdateData.id
                    : null;

                await this.$store
                    .dispatch('getTollFreePhoneVerify', { profile_id: id, number_id })
                    .catch(async (error) => {
                        if (error.response.status === 400 && Array.isArray(error.response.data)) {
                            for (const message of error.response.data) {
                                await this.$store.dispatch('showToast', {
                                    type: 'danger',
                                    message,
                                });

                                await this.$delay(100);
                            }

                            throw error;
                        }
                    });

                return;
            }

            await this.$store.dispatch('getTollFreeVerify', id);
        },

        handleClosed() {
            this.$store.dispatch('setTollFreeDataForRecoveredVerification', null);
            this.$store.dispatch('handleShowTollFreeVerificationModal', {
                status: false,
                type: null,
                disabledNumbersStep: false,
                phoneUpdateData: null,
                item: null,
            });
            this.$emit('closed');
            this.reset();
            this.$store.dispatch(
                'updateComplianceModalsOrder',
                this.complianceModalsOrder.filter((modal) => modal !== TOLL_FREE_OPTION_NAME)
            );
        },

        handleMonthlyDropdownStatus(status) {
            this.isActiveMonthlyDropdown = status;
        },

        handleOptIn(opt_in_data, files, optionsState) {
            this.opt_in_options_state = optionsState;
            this.opt_in_files = files;

            this.isOptInCompleted = optionsState && !!files.length;
            this.form.opt_in_methods = [];
            this.opt_in_data = opt_in_data;

            for (const key in opt_in_data) {
                const item = opt_in_data[key];

                this.form.opt_in_methods.push(item.opt_in_method_id);
            }
        },

        async handleDeleteOptInFile({ file, handleDelete }) {
            try {
                if (file.is_local) {
                    handleDelete();
                } else {
                    file.loading = true;
                    await this.$store.dispatch('deleteOptInFiles', file.id);
                    await this.$store.dispatch('fetchTollFreeProfile');
                    await this.deleteOptInDataFile(file.opt_in_method_id, file);
                    await handleDelete();
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleDeleteOptInFile');
            } finally {
                file.loading = false;
            }
        },

        handleUpdateState([key, value]) {
            this[key] = value;
        },

        async handleUploadFiles() {
            try {
                const callbacks = [];

                const files = this.opt_in_files.filter((item) => item.is_local);
                for (let i = 0; i < files.length; i++) {
                    callbacks.push(async () => {
                        const file = files[i];

                        try {
                            const formData = new FormData();

                            formData.append('file', file.file, file.file.name);
                            formData.append('opt_in_method_id', file.opt_in_method_id);
                            formData.append('profile_id', this.currentTollFreeProfileId);
                            formData.append('relatable_service_type', file.relatable_service_type);
                            formData.append('fileName', file.file.name);
                            formData.append('fileSize', file.file.size);

                            file.loading = true;
                            file.error = '';
                            const fileResponse = await this.$store.dispatch(
                                'uploadOptInFiles',
                                formData
                            );

                            file.id = fileResponse.id;
                            file.image = fileResponse.source_url;
                            file.loading = false;
                            file.is_local = false;

                            this.addOptInDataFile(file.opt_in_method_id, fileResponse);
                        } catch (e) {
                            this.$log.error(e, this.$options.__file, 'handleUploadFiles');

                            const error = e?.response?.data?.file;

                            if (error) {
                                file.error = Array.isArray(error) ? error.join('<br />') : '';
                            }

                            throw e;
                        } finally {
                            file.loading = false;
                        }
                    });
                }

                await Promise.all(callbacks.map((callback) => callback()));
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleUploadFiles');

                throw e;
            }
        },

        addOptInDataFile(method, file) {
            this.opt_in_data_server = this.opt_in_data_server.map((item) => {
                if (method === item.opt_in_method_id) {
                    item.files.push(file);
                }

                return item;
            });
        },

        deleteOptInDataFile(method, file) {
            this.opt_in_data_server = this.opt_in_data_server.map((item) => {
                if (method === item.opt_in_method_id) {
                    item.files = item.files.filter((f) => f.id !== file.id);
                }

                return item;
            });
        },

        async requestIntoSteps(field, step) {
            const replaceField = `${field.replace(/_/gi, '-')}`;

            try {
                this.form.busy = true;
                const currentFormData = cloneDeep(this.form[field]);

                Object.keys(currentFormData).forEach((key) => {
                    if ([null, ''].includes(currentFormData[key])) {
                        delete currentFormData[key];
                    }
                });

                const config = {
                    replaceField,
                    form: this.form,
                    params: {
                        ...(field === 'points_of_contact'
                            ? { contact_person: currentFormData }
                            : currentFormData),
                        should_update_stripe_customer: true,
                    },
                };

                await this.$store.dispatch('updateBusinessProfileTollFreeAIInformation', config);
                this.form.errors.clear();

                if (!step) {
                    this.form.successful = true;
                    this.$store.dispatch('showToast', {
                        type: 'success',
                        message: 'Business Profile updated successfully',
                    });

                    setTimeout(() => {
                        this.handleClosed();
                    }, 1500);
                } else {
                    this.setStep(this.stepsAccess[this.currentStepIndex + 1]);
                    this.form.successful = false;
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'requestIntoSteps');
            } finally {
                this.form.busy = false;
            }
        },

        async runGeneratingUseCase() {
            this.isGenerating = true;
            const useCase = this.tollFreeFormFieldsUseCases.find(
                (item) => item.id === Number(this.form.use_case_id)
            );

            const responseSummaryMessage = await this.$store.dispatch('getTollFreeSummaryMessage', {
                companyName: this.form.business_information.business_name,
                website: this.form.business_information.website_url,
            });

            if (responseSummaryMessage) {
                await this.$store.dispatch('getTollFreeSampleMessage', {
                    companyName: this.form.business_information.business_name,
                    useCase: useCase?.title,
                    description: responseSummaryMessage?.result_message,
                    website: this.form.business_information.website_url,
                });
            }

            this.isGenerating = false;
        },

        applyState(state_id) {
            if (this.$refs.stateSelect) {
                this.$refs.stateSelect.updateValue(state_id);
            }
        },

        setCompanyStatesList() {
            this.arrStatesOfCountry = this.arrStates.filter(
                (o) => o.country_id === this.form.business_information.country
            );
        },

        selectCountry(country) {
            this.form.business_information.country = country?.key || country;
            this.setCompanyStatesList();
            this.form.business_information.state_id = null;
        },

        insertCompanyInfo(data) {
            this.applyState(data?.state || data?.state_id);
            this.form.business_information.business_name = data?.name || data?.business_name;
            this.form.business_information.street_address_1 =
                data?.street1 || data?.street_address_1;
            this.form.business_information.street_address_2 =
                data?.street2 || data?.street_address_2;
            this.form.business_information.city = data?.city;
            this.form.business_information.postal_code =
                data?.zip || data?.zip_code || data?.postal_code;
            this.form.business_information.state_id = data?.state || data?.state_id;
            this.form.business_information.country = data?.country || 'US';
            this.form.business_information.website_url = data?.website_url;
            this.form.business_information.ein = data?.ein;
        },

        getTollFreeCaseStep(data) {
            const businessInformation = this.profileBusiness?.businessInformation;
            const pointsOfContacts = this.profileBusiness?.pointsOfContacts[0];

            if (data) {
                const isStep1Completed = Boolean(
                    data.numbers?.length === 1 ||
                        (data.numbers?.length > 1 && data.explanation_description)
                );
                const isStep2Completed =
                    isStep1Completed &&
                    Boolean(
                        businessInformation &&
                            businessInformation.business_name &&
                            (businessInformation.street_address_1 ||
                                businessInformation.street_address_2) &&
                            businessInformation.city &&
                            businessInformation.zip_code &&
                            businessInformation.state_id &&
                            businessInformation.website_url
                    );

                const isStep3Completed =
                    isStep2Completed &&
                    Boolean(
                        pointsOfContacts &&
                            pointsOfContacts.first_name &&
                            pointsOfContacts.last_name &&
                            pointsOfContacts.email &&
                            pointsOfContacts.business_title &&
                            pointsOfContacts.job_position?.id &&
                            pointsOfContacts.phone_number
                    );

                const isStep4Completed =
                    isStep3Completed &&
                    Boolean(
                        data.use_case_description &&
                            data.use_case_id &&
                            data.monthly_volume &&
                            data.sample_message_1
                    );

                let countFiles = 0;

                if (data.opt_in_data?.length) {
                    data.opt_in_data.forEach((tollFreeCase) => {
                        if (tollFreeCase.files?.length) {
                            countFiles++;
                        }
                    });
                }

                const countOptInData = data.opt_in_data?.length;
                const isHasAllFiles = countFiles === countOptInData;

                const isStep5Completed = Boolean(
                    isStep4Completed &&
                        data.opt_in_methods?.length &&
                        countOptInData &&
                        isHasAllFiles
                );

                if (!isStep1Completed || (isStep1Completed && !isStep2Completed)) {
                    return 1;
                } else if (isStep2Completed && !isStep3Completed) {
                    return 2;
                } else if (isStep3Completed && !isStep4Completed) {
                    return 3;
                } else if (isStep4Completed && !isStep5Completed) {
                    return 4;
                } else if (isStep5Completed) {
                    return 5;
                } else {
                    return 1;
                }
            }
        },

        reset() {
            this.form.reset();
            this.opt_in_data = {};
            this.opt_in_data_server = [];
            this.opt_in_data_error = null;
            this.opt_in_options_state = false;
            this.opt_in_files = [];
            this.isLoading = false;
            this.isGenerating = false;
            this.currentStepIndex = 0;
            this.arrStates = [];
            this.arrStatesOfCountry = [];
            this.arrJobPositions = [];
            this.isActiveMonthlyDropdown = false;
            this.validateLoadingFileOptIn = false;
            this.isOptInCompleted = false;
            this.showOptInPolicyOptionsError = false;
            this.showOptInFilesError = false;
            this.isAllTollFreeNumbersVerified = false;
            this.isCreateTollFreeProfileClicked = false;
            this.isOnTrialLocal = false;
            this.currentTollFreeProfileId = null;
        },

        checkAvailableNumbersList() {
            this.isAllTollFreeNumbersVerified =
                this.currentStep === MODAL_TYPES.ADD_NUMBERS &&
                this.getAvailableNumbers?.length === 0;
        },

        dropdownItem(item, escape) {
            return `<div class='selectize-contact'>
                        ${escape(item.label)}
                    </div>`;
        },

        dropdownOption(item, escape) {
            return `<div class='selectize-contact'>
                        <div class='selectize-contact-info'>
                            <span>${escape(item.label)}</span>
                        </div>
                    </div>`;
        },

        dropdownJobPositionItem(item) {
            return `<div class="selectize-contact is-small">
                <span>${item.name}</span>
            </div>`;
        },

        dropdownJobPositionOption(item) {
            return `<div class="selectize-country">
                <span>${item.name}</span>
            </div>`;
        },
    },
};
</script>

<style lang="scss">
@import 'styles.module';
</style>
